import { cx } from "../../utils/classnames";
import type { TabStyle, TabSize, Orientation } from "./types";

export const containerClass = {
  ghost: {
    horizontal: "flex gap-1 items-center",
    vertical: "flex flex-col gap-1 items-start"
  },
  "ghost-pink": {
    horizontal: "flex gap-1 items-center",
    vertical: "flex flex-col gap-1 items-start"
  },
  outline: {
    horizontal:
      "bg-alpha-20 border border-alpha-80 rounded-[10px] p-1 flex gap-1 items-center",
    vertical:
      "bg-alpha-20 border border-alpha-80 rounded-[10px] p-1 flex flex-col gap-1 items-start"
  },
  line: {
    horizontal: "border-b border-alpha-80",
    vertical: "flex flex-col gap-2 items-start"
  }
};

export const tabSizeClass = {
  ghost: { sm: "h-9 text-sm", md: "h-11 text-base" },
  "ghost-pink": { sm: "h-9 text-sm", md: "h-11 text-base" },
  outline: { sm: "h-9 text-sm", md: "h-11 text-base" },
  line: {
    sm: { horizontal: "text-sm", vertical: "h-6 text-sm" },
    md: { horizontal: "text-base", vertical: "h-8 text-base" }
  }
};

export const tabActiveClass = {
  ghost: "bg-alpha-40 text-content-1000 hover:bg-alpha-80",
  "ghost-pink": "bg-primary-20 text-primary-500",
  outline: "bg-white text-content-1000 shadow-sm",
  line: "text-primary-500"
};

export const tabInactiveClass = {
  ghost: "text-content-1000 hover:bg-alpha-80",
  "ghost-pink": "text-content-1000 hover:bg-alpha-40 hover:text-content-700",
  outline: "text-content-1000 hover:bg-white hover:shadow-sm",
  line: "text-content-1000 hover:text-primary-500"
};

export const tabDisabledClass = "text-content-200 cursor-not-allowed";

// --- TabButtonStyles class ---

interface TabButtonStylesOptions {
  tabStyle: TabStyle;
  size: TabSize;
  orientation: Orientation;
  active: boolean;
  disabled?: boolean;
  fullWidth: boolean;
}

export class TabButtonStyles {
  private tabStyle: TabStyle;
  private size: TabSize;
  private orientation: Orientation;
  private active: boolean;
  private disabled: boolean;
  private fullWidth: boolean;

  constructor(opts: TabButtonStylesOptions) {
    this.tabStyle = opts.tabStyle;
    this.size = opts.size;
    this.orientation = opts.orientation;
    this.active = opts.active;
    this.disabled = opts.disabled ?? false;
    this.fullWidth = opts.fullWidth;
  }

  private get isLine() {
    return this.tabStyle === "line";
  }

  private get isHorizontal() {
    return this.orientation === "horizontal";
  }

  private get isVertical() {
    return this.orientation === "vertical";
  }

  get sizeClass() {
    if (this.isLine) return tabSizeClass.line[this.size][this.orientation];
    return tabSizeClass[this.tabStyle][this.size];
  }

  get stateClass() {
    if (this.disabled) return tabDisabledClass;
    if (this.active) return tabActiveClass[this.tabStyle];
    return tabInactiveClass[this.tabStyle];
  }

  get borderClass() {
    if (!this.isLine) return "";
    const dir = this.isHorizontal ? "border-b-2" : "border-l-2";
    if (this.disabled) return cx(dir, "border-transparent");
    if (this.active) return cx(dir, "border-primary-500");
    return cx(dir, "border-transparent hover:border-primary-500");
  }

  get paddingClass() {
    if (!this.isLine) return "px-3 py-2 rounded-md";
    return this.isHorizontal ? "pb-3 px-1" : "px-3 py-0.5";
  }

  get widthClass() {
    if (this.isVertical) return "w-full";
    return this.fullWidth ? "flex-1" : "shrink-0";
  }

  get layoutClass() {
    return cx(
      "flex gap-1.5 items-center",
      this.isHorizontal ? "justify-center" : "overflow-clip"
    );
  }

  get focusClass() {
    if (this.isLine) return "";
    return "focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2";
  }
}

export const counterStyles = {
  active: {
    ghost:
      "bg-white border border-alpha-80 rounded-md shadow-xs text-content-700 px-1.5 py-0.5 text-[13px]",
    "ghost-pink":
      "bg-white border border-primary-500 rounded-md shadow-xs text-primary-500 px-1.5 py-0.5 text-[13px]",
    outline:
      "bg-white border border-alpha-80 rounded-md shadow-xs text-content-1000 px-1.5 py-0.5 text-[13px]",
    line: "bg-primary-20 border border-primary-20 rounded-full text-primary-500 px-2 py-0.5"
  },
  inactive: {
    ghost:
      "bg-white border border-alpha-80 rounded-md shadow-xs text-content-700 px-1.5 py-0.5 text-[13px]",
    "ghost-pink":
      "bg-white border border-alpha-80 rounded-md shadow-xs text-content-700 px-1.5 py-0.5 text-[13px]",
    outline:
      "bg-white border border-alpha-80 rounded-md shadow-xs text-content-1000 px-1.5 py-0.5 text-[13px]",
    line: "bg-alpha-20 border border-alpha-40 rounded-full text-content-700 px-2 py-0.5 group-hover:bg-primary-20 group-hover:border-primary-20 group-hover:text-primary-500"
  },
  disabled: {
    ghost:
      "bg-white border border-alpha-80 rounded-md shadow-xs text-content-200 px-1.5 py-0.5 text-[13px]",
    "ghost-pink":
      "bg-white border border-alpha-80 rounded-md shadow-xs text-content-200 px-1.5 py-0.5 text-[13px]",
    outline:
      "bg-white border border-alpha-80 rounded-md shadow-xs text-content-200 px-1.5 py-0.5 text-[13px]",
    line: "bg-alpha-20 border border-alpha-40 rounded-full text-content-200 px-2 py-0.5"
  }
};
